/**
 * Client-side helper that reliably triggers a save-to-disk download.
 *
 * Supabase Storage signed URLs are served with `Content-Disposition: inline`
 * unless the URL carries a `download` query param, and because they are
 * cross-origin (*.supabase.co) the browser ignores the anchor `download`
 * attribute — so renderable types (PDF, CSV, images, …) open in a new tab
 * instead of saving. This module fixes the download click without touching
 * the URLs used for inline previews.
 */

const SIGNED_OBJECT_PATH_PREFIX = "/storage/v1/object/sign/";

/** Origin of this project's configured Supabase instance, or null if unset/invalid. */
function getTrustedSupabaseOrigin(): string | null {
  const configuredUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!configuredUrl) return null;
  try {
    return new URL(configuredUrl).origin;
  } catch {
    return null;
  }
}

/**
 * True when `url` is a signed Storage object URL for *this project's* Supabase
 * instance — both the origin and the object path are checked, so a URL can't
 * be misclassified just by containing a similar-looking path fragment.
 */
export function isSupabaseSignedUrl(url: string): boolean {
  const trustedOrigin = getTrustedSupabaseOrigin();
  if (!trustedOrigin) return false;
  try {
    const parsed = new URL(url);
    return (
      parsed.origin === trustedOrigin &&
      parsed.pathname.startsWith(SIGNED_OBJECT_PATH_PREFIX)
    );
  } catch {
    return false;
  }
}

/**
 * Appends `download=<filename>` to a signed URL so Storage serves it with
 * `Content-Disposition: attachment`. This mirrors what supabase-js does for
 * `createSignedUrl(path, ttl, { download })`; the token stays valid because
 * it signs the object path, not the query string.
 */
export function withDownloadParam(url: string, filename: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}download=${encodeURIComponent(filename)}`;
}

function triggerAnchorDownload(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function isSameOrigin(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Triggers a save-to-disk download of `url` as `filename`.
 *
 * - Supabase signed URLs get the `download` query param appended so the
 *   response itself is an attachment.
 * - blob:/data:/same-origin URLs use a plain anchor with the `download`
 *   attribute (which browsers honour for same-origin content).
 * - Other cross-origin URLs are fetched into a blob and downloaded via an
 *   object URL; if the fetch fails, falls back to opening in a new tab so
 *   the user still gets the file.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  if (isSupabaseSignedUrl(url)) {
    triggerAnchorDownload(withDownloadParam(url, filename), filename);
    return;
  }

  if (url.startsWith("blob:") || url.startsWith("data:") || isSameOrigin(url)) {
    triggerAnchorDownload(url, filename);
    return;
  }

  // Cross-origin: the anchor `download` attribute is ignored, so pull the
  // bytes into a same-origin object URL first.
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Download failed with status ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    triggerAnchorDownload(objectUrl, filename);
    URL.revokeObjectURL(objectUrl);
  } catch {
    // Last resort — at least surface the file to the user.
    window.open(url, "_blank", "noopener");
  }
}
